import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import {
  FaPlus,
  FaSearch,
  FaList,
  FaChevronDown,
  FaChevronUp,
  FaTrash,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/hireDetailsListing";

const HireVehicleDetailsListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const navigate = useNavigate();

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const branchActive =
    sessionStorage.getItem("BranchActive") ||
    sessionStorage.getItem("branchActive") ||
    "1";

  const [branches, setBranches] = useState([]);
  const [stores, setStores] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);

  const [selectedBranchId, setSelectedBranchId] = useState(branchId || "");
  const [storeId, setStoreId] = useState("Select");
  const [vehicleType, setVehicleType] = useState("Select");
  const [hiredFrom, setHiredFrom] = useState("");
  const [vehicleCode, setVehicleCode] = useState("");
  const [vehicleRegNo, setVehicleRegNo] = useState("");
  const [vehicleName, setVehicleName] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [rights, setRights] = useState({
    canAccess: true,
    canAdd: true,
    canDelete: true,
  });

  const [selectedRows, setSelectedRows] = useState([]);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const normalizeValue = (val) => {
    if (!val || val === "Select") return "";
    return String(val);
  };

  const normalizeRow = (row) => ({
    ...row,
    vehicleDetId:
      row?.fld_vehicledetid ||
      row?.vehicledetid ||
      row?.VehicleDetId ||
      row?.VehicleDetID ||
      0,
    vehicleId: row?.fld_vehicleid || row?.vehicleid || row?.VehicleID || 0,
    vehicleCode:
      row?.fld_vehicle_code ||
      row?.vehicleCode ||
      row?.VehicleCode ||
      row?.["Vehicle Code"] ||
      "-",
    vehicleName:
      row?.fld_vehicle_name ||
      row?.vehicleName ||
      row?.VehicleName ||
      row?.["Vehicle Name"] ||
      "-",
    vehicleRegNo:
      row?.fld_vehicle_regno ||
      row?.vehicleRegNo ||
      row?.VehicleRegNo ||
      row?.["Vehicle Registration Number"] ||
      "-",
    vehicleSubtype:
      row?.fld_vehicle_subtypename ||
      row?.vehicleSubtype ||
      row?.VehicleSubtype ||
      row?.["Vehicle Subtype"] ||
      "-",
    hiredFrom:
      row?.fld_hiredfrom ||
      row?.hiredFrom ||
      row?.HiredFrom ||
      row?.["Hired From"] ||
      "-",
    basis:
      row?.fld_vehicle_basistype ||
      row?.fld_vehicle_basis ||
      row?.vehicleBasis ||
      row?.VehicleBasis ||
      row?.["Vehicle Basis"] ||
      "-",
    status:
      row?.Status ||
      row?.fld_vehicle_status ||
      row?.vehicleStatus ||
      row?.VehicleStatus ||
      row?.["Vehicle Status"] ||
      "Open",
  });

  const loadRights = async () => {
    try {
      const r = await axios.get(`${API_BASE}/rights`, {
        params: { loginUserId: userId },
        headers: commonHeaders,
      });

      if (r.data?.success) {
        const data = r.data?.data || {};
        setRights({
          canAccess: data?.rights ?? true,
          canAdd: data?.add ?? true,
          canDelete: data?.del ?? true,
        });
      }
    } catch (err) {
      console.error(err);
      setRights({ canAccess: true, canAdd: true, canDelete: true });
    }
  };

  const loadMeta = async (branchParam) => {
    try {
      const selected =
        branchType === "HO"
          ? branchParam || selectedBranchId || branchId
          : branchId;

      const r = await axios.get(`${API_BASE}/meta`, {
        params: {
          branchId,
          loginUserId: userId,
          branchType,
          selectedBranchId: selected,
        },
        headers: commonHeaders,
      });

      if (!r.data?.success) throw new Error(r.data?.message || "Meta failed");

      const data = r.data?.data || {};
      setBranches(Array.isArray(data?.branches) ? data.branches : []);
      setStores(Array.isArray(data?.stores) ? data.stores : []);
      setVehicleTypes(Array.isArray(data?.vehicleTypes) ? data.vehicleTypes : []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load dropdown data!");
      setBranches([]);
      setStores([]);
      setVehicleTypes([]);
    }
  };

  const fetchList = async (mode) => {
    if (!branchId) {
      toast.error("branchId missing in localStorage!");
      return;
    }

    setLoading(true);
    setSelectedRows([]);
    setHasSearched(true);

    try {
      const selected =
        branchType === "HO" ? selectedBranchId || branchId : branchId;

      const r = await axios.get(`${API_BASE}/list`, {
        params: {
          mode,
          branchId,
          loginUserId: userId,
          branchType,
          selectedBranchId: selected,
          storeId: normalizeValue(storeId),
          vehicleType: normalizeValue(vehicleType),
          hiredFrom,
          vehicleCode,
          vehicleRegNo,
          vehicleName,
        },
        headers: commonHeaders,
      });

      const data = Array.isArray(r.data?.data) ? r.data.data : [];
      setRows(data.map(normalizeRow));
      if (data.length === 0) toast.info("No Records found");
    } catch (err) {
      console.error(err);
      toast.error(
        mode === "all" ? "Failed to load view all list" : "Failed to load list"
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadList = () => fetchList("search");
  const loadViewAll = () => fetchList("all");

  const onBranchChange = async (e) => {
    const val = e.target.value;
    setSelectedBranchId(val);
    setStoreId("Select");
    await loadMeta(val);
  };

  const toggleRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const allSelected =
    rows.length > 0 && selectedRows.length === rows.length;

  const toggleAll = () => {
    if (allSelected) setSelectedRows([]);
    else setSelectedRows(rows.map((r) => Number(r.vehicleDetId)).filter(Boolean));
  };

  const clearFilters = () => {
    setStoreId("Select");
    setVehicleType("Select");
    setHiredFrom("");
    setVehicleCode("");
    setVehicleRegNo("");
    setVehicleName("");
    toast.info("Filters cleared");
  };

  const onEdit = (row) => {
    navigate(
      `/vehicle/HireVehicle_DetailsEntry?vehicledetid=${row.vehicleDetId || ""}&vehicleid=${
        row.vehicleId || ""
      }&vehicleCode=${encodeURIComponent(row.vehicleCode || "")}`
    );
  };

  const onDeleteSelected = async () => {
    if (!rights.canDelete) {
      toast.error("You don't have delete rights.");
      return;
    }

    if (branchActive !== "1") {
      toast.error("Branch is inactive. Delete not allowed.");
      return;
    }

    const ids = selectedRows
      .map((x) => Number(x))
      .filter((x) => Number.isFinite(x) && x > 0);

    if (!ids.length) {
      toast.warn("Please select at least one record to delete.");
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to delete ${ids.length} record(s)?`
    );
    if (!ok) return;

    try {
      const r = await axios.post(
        `${API_BASE}/delete`,
        { vehicledetIds: ids },
        { headers: commonHeaders }
      );

      if (r.data?.success) {
        toast.success(r.data?.message || "Deleted successfully!");
        await loadList();
      } else {
        toast.error(r.data?.message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await loadRights();
        await loadMeta(selectedBranchId || branchId);
        await loadList();
      } catch (err) {
        console.error(err);
        toast.error("Failed to initialize page");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  if (!rights.canAccess) {
    return (
      <VehicleLayoutWrapper>
        <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
          <div className="p-4 rounded-lg border border-red-300 bg-red-50 text-red-700">
            You don’t have rights to access this page.
          </div>
        </div>
      </VehicleLayoutWrapper>
    );
  }

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              HIRE VEHICLE DETAILS LISTING
            </span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* HEADER */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Hire Vehicle Details Listing
            </h2>

            <div className="flex items-center gap-3 flex-wrap">
              {rights.canAdd && branchActive === "1" && (
                <button
                  onClick={() => navigate("/vehicle/HireVehicle_DetailsEntry")}
                  className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaPlus />
                  Add New
                </button>
              )}

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* SEARCH CRITERIA */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={clearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={loadViewAll}
                    disabled={loading}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    <FaList />
                    View All
                  </button>

                  <button
                    onClick={loadList}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    value={selectedBranchId}
                    onChange={onBranchChange}
                    disabled={branchType !== "HO"}
                    className={`${inputClass} ${
                      branchType !== "HO" ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    <option value="">Select</option>
                    {branches.map((b, idx) => {
                      const id =
                        b?.fld_Branchid ||
                        b?.BranchId ||
                        b?.branchid ||
                        b?.fld_branchid ||
                        "";
                      const name =
                        b?.fld_BranchName ||
                        b?.BranchName ||
                        b?.branchname ||
                        id;
                      return (
                        <option key={idx} value={id}>
                          {name}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    value={storeId}
                    onChange={(e) => setStoreId(e.target.value)}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {stores.map((s, idx) => {
                      const id = s?.fld_storeid || s?.StoreId || s?.storeid || "";
                      const name =
                        s?.fld_storename || s?.StoreName || s?.storename || id;
                      return (
                        <option key={idx} value={id}>
                          {name}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Subtype</label>
                  <select
                    value={vehicleType}
                    onChange={(e) => setVehicleType(e.target.value)}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {vehicleTypes.map((v, idx) => {
                      const id =
                        v?.fld_vehicle_subtypeid ||
                        v?.VehicleSubtypeId ||
                        v?.subtypeid ||
                        v?.id ||
                        "";
                      const name =
                        v?.fld_vehicle_subtypename ||
                        v?.VehicleSubtypeName ||
                        v?.subtypename ||
                        v?.name ||
                        id;
                      return (
                        <option key={idx} value={id}>
                          {name}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Hired From</label>
                  <input
                    type="text"
                    value={hiredFrom}
                    onChange={(e) => setHiredFrom(e.target.value)}
                    placeholder="Hired From"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    value={vehicleCode}
                    onChange={(e) => setVehicleCode(e.target.value)}
                    placeholder="Vehicle Code"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Reg No</label>
                  <input
                    type="text"
                    value={vehicleRegNo}
                    onChange={(e) => setVehicleRegNo(e.target.value)}
                    placeholder="Vehicle Reg No"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    value={vehicleName}
                    onChange={(e) => setVehicleName(e.target.value)}
                    placeholder="Vehicle Name"
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          )}

          {/* TABLE */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {hasSearched ? "No Records found" : "Please search to view records"}
                </div>
              </div>
            ) : (
              <>
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Code</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Name</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Reg No</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Subtype</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Hired From</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Basis</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Status</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleAll}
                          className="w-4 h-4 cursor-pointer"
                          title="Select All"
                        />
                      </th>
                    </tr>
                  </thead>

                  <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                    {rows.map((row, idx) => {
                      const id = Number(row.vehicleDetId);
                      const checked = selectedRows.includes(id);

                      return (
                        <tr
                          key={id || idx}
                          className={`${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-white"
                              : isDark
                              ? "bg-gray-750"
                              : "bg-gray-50"
                          } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                        >
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {idx + 1}
                          </td>
                          <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                            {row.vehicleCode}
                          </td>
                          <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                            {row.vehicleName}
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {row.vehicleRegNo}
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {row.vehicleSubtype}
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {row.hiredFrom}
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {row.basis}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                String(row.status).toLowerCase() === "open"
                                  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100"
                                  : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100"
                              }`}
                            >
                              {row.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => onEdit(row)}
                              disabled={!(rights.canAdd && branchActive === "1")}
                              className={`text-yellow-500 hover:text-yellow-600 text-lg transition ${
                                !(rights.canAdd && branchActive === "1")
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                              title="Edit"
                            >
                              ✏️
                            </button>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleRow(id)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {rights.canDelete && branchActive === "1" && (
                  <div className="p-4">
                    <button
                      onClick={onDeleteSelected}
                      disabled={selectedRows.length === 0}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                        selectedRows.length === 0
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      } bg-red-600 hover:bg-red-700 text-white`}
                    >
                      <FaTrash />
                      Remove
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default HireVehicleDetailsListing; 